import {
  IPendingSignatureItem,
  IPendingSignaturesData,
  IPendingSignaturesSigning,
  OwnerDocumentSignMode,
  OwnerDocumentStatus,
} from "../types/ownerDashboard.types";
import { normalizePagination } from "./normalizePagination";

const DEFAULT_SIGNING: IPendingSignaturesSigning = {
  provider: "clicksign_v1",
  signer_key_endpoint: "/api/client/signer-key/{rentProcessId}",
  download_endpoint: "/api/client/rent-contract/dowload/{rentProcessId}",
  clicksign_download_endpoint:
    "/api/client/owner/dashboard/clicksign-envelopes/{envelopeId}/download",
  clicksign_notify_endpoint:
    "/api/client/owner/dashboard/clicksign-envelopes/{envelopeId}/notify",
  embedded_widget_script: "/js/clicksign-build-embedded.js",
};

function asFiniteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function asNullableString(value: unknown): string | null {
  const text = String(value ?? "").trim();
  return text || null;
}

function normalizeDocumentStatus(value: unknown, signedByUser = false): OwnerDocumentStatus {
  const status = String(value ?? "").toLowerCase();

  // ClickSign 3.0 uses running|closed; legacy payloads may still send pending|signed.
  if (status === "signed" || status === "closed") {
    return "signed";
  }

  if (status === "canceled") {
    return "canceled";
  }

  if (status === "pending" || status === "running") {
    return "pending";
  }

  return signedByUser ? "signed" : "pending";
}

function normalizeSignMode(value: unknown): OwnerDocumentSignMode {
  const mode = String(value ?? "").toLowerCase();

  if (mode === "email" || mode === "embedded") {
    return mode;
  }

  return null;
}

function countByDocumentStatus(items: IPendingSignatureItem[]) {
  return items.reduce(
    (counts, item) => {
      if (item.document_status === "signed") {
        counts.signed += 1;
      } else if (item.document_status === "canceled") {
        counts.canceled += 1;
      } else {
        counts.pending += 1;
      }
      return counts;
    },
    { pending: 0, signed: 0, canceled: 0 }
  );
}

function normalizeRentContractItem(value: unknown): IPendingSignatureItem | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const id = asFiniteNumber(record.id);
  const rentProcessId = asFiniteNumber(record.rent_process_id ?? record.rentProcessId);
  const propertyId = asFiniteNumber(record.property_id ?? record.propertyId);

  if (id == null || rentProcessId == null) {
    return null;
  }

  const signedByUser = Boolean(record.signed_by_user);
  const documentStatus = normalizeDocumentStatus(record.document_status, signedByUser);
  const rentProcessStatus = asNullableString(record.rent_process_status);
  const propertyTitle = asNullableString(record.property_title);
  const propertyAddress = asNullableString(record.property_address);

  return {
    id: `rent-${id}`,
    source: "rent_contract",
    title: propertyTitle || propertyAddress || `Contrato ${rentProcessId}`,
    subtitle: propertyTitle && propertyAddress ? propertyAddress : null,
    tenant_name: asNullableString(record.tenant_name),
    rent_process_id: rentProcessId,
    property_id: propertyId,
    contract_key: asNullableString(record.contract_key),
    envelope_id: null,
    rent_process_status: rentProcessStatus,
    signed_by_user: signedByUser,
    document_status: documentStatus,
    can_sign:
      typeof record.can_sign === "boolean"
        ? record.can_sign
        : documentStatus === "pending" && rentProcessStatus === "CONTRACT_DONE",
    can_download:
      typeof record.can_download === "boolean"
        ? record.can_download
        : documentStatus === "signed" || rentProcessStatus === "CONTRACT_FINISHED",
    created_at: String(record.created_at ?? "").trim(),
    request_signature_key: null,
    signer_key: null,
    sign_mode: "embedded",
    download_url: null,
    signed_file_url: null,
    ziped_file_url: null,
  };
}

function normalizeClickSignDocument(value: unknown): IPendingSignatureItem | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;
  const key = asNullableString(record.key ?? record.envelope_id);

  if (!key) {
    return null;
  }

  const localContract =
    record.local_contract && typeof record.local_contract === "object"
      ? (record.local_contract as Record<string, unknown>)
      : null;
  const downloads =
    record.downloads && typeof record.downloads === "object"
      ? (record.downloads as Record<string, unknown>)
      : {};
  // ClickSign 3.0 does not return per-signer signed flags on GET /signers.
  // Document completion comes from document_status/status + owner_signed only.
  const ownerSigned = Boolean(record.owner_signed);
  const documentStatus = normalizeDocumentStatus(
    record.document_status ?? record.status,
    ownerSigned
  );
  const rentProcessId =
    asFiniteNumber(record.rent_process_id) ??
    asFiniteNumber(localContract?.rent_process_id);
  const signerKey =
    asNullableString(record.signer_key) ??
    asNullableString(record.request_signature_key);
  const signMode = normalizeSignMode(record.sign_mode) ?? (signerKey ? "embedded" : "email");
  const signedFileUrl = asNullableString(downloads.signed_file_url);
  const zipedFileUrl = asNullableString(downloads.ziped_file_url);
  const downloadUrl = asNullableString(record.download_url);
  const title =
    asNullableString(record.name) ||
    asNullableString(record.filename) ||
    `Documento ${key.slice(0, 8)}`;

  const canSign =
    typeof record.can_sign === "boolean"
      ? record.can_sign
      : documentStatus === "pending" && !ownerSigned;
  const canDownload =
    typeof record.can_download === "boolean"
      ? record.can_download
      : Boolean(downloadUrl || signedFileUrl || zipedFileUrl) || documentStatus === "signed";

  return {
    id: `clicksign-${key}`,
    source: "clicksign_envelope",
    title,
    subtitle: null,
    tenant_name: asNullableString(localContract?.tenant_name),
    rent_process_id: rentProcessId,
    property_id: asFiniteNumber(localContract?.property_id),
    contract_key: asNullableString(record.envelope_id ?? record.key),
    envelope_id: asNullableString(record.envelope_id ?? record.key),
    rent_process_status: asNullableString(localContract?.rent_process_status),
    signed_by_user: ownerSigned,
    document_status: documentStatus,
    can_sign: canSign,
    can_download: canDownload,
    created_at: String(record.uploaded_at ?? record.updated_at ?? "").trim(),
    request_signature_key: asNullableString(record.request_signature_key),
    signer_key: signerKey,
    sign_mode: signMode,
    download_url: downloadUrl,
    signed_file_url: signedFileUrl,
    ziped_file_url: zipedFileUrl,
  };
}

function normalizeSigning(value: unknown): IPendingSignaturesSigning {
  if (!value || typeof value !== "object") {
    return DEFAULT_SIGNING;
  }

  const record = value as Record<string, unknown>;

  return {
    provider: String(record.provider ?? DEFAULT_SIGNING.provider),
    signer_key_endpoint: String(
      record.signer_key_endpoint ?? DEFAULT_SIGNING.signer_key_endpoint
    ),
    download_endpoint: String(
      record.download_endpoint ?? DEFAULT_SIGNING.download_endpoint
    ),
    clicksign_download_endpoint:
      asNullableString(record.clicksign_download_endpoint) ??
      DEFAULT_SIGNING.clicksign_download_endpoint,
    clicksign_notify_endpoint:
      asNullableString(record.clicksign_notify_endpoint) ??
      DEFAULT_SIGNING.clicksign_notify_endpoint,
    embedded_widget_script: String(
      record.embedded_widget_script ?? DEFAULT_SIGNING.embedded_widget_script
    ),
  };
}

export function normalizePendingSignatures(raw: unknown): IPendingSignaturesData {
  if (!raw || typeof raw !== "object") {
    return {
      total: 0,
      pending_count: 0,
      signed_count: 0,
      canceled_count: 0,
      items: [],
      signing: DEFAULT_SIGNING,
      clicksign_configured: null,
      clicksign_error: null,
    };
  }

  const record = raw as Record<string, unknown>;
  const clicksign =
    record.clicksign && typeof record.clicksign === "object"
      ? (record.clicksign as Record<string, unknown>)
      : null;

  const rentItems = (
    Array.isArray(record.items)
      ? record.items
      : Array.isArray(record.pending_signatures)
        ? record.pending_signatures
        : []
  )
    .map(normalizeRentContractItem)
    .filter((item): item is IPendingSignatureItem => item != null);

  const rawClickSignDocuments = clicksign?.documents;
  const clicksignDocuments = Array.isArray(rawClickSignDocuments)
    ? rawClickSignDocuments
    : [];
  const clicksignItems = clicksignDocuments
    .map(normalizeClickSignDocument)
    .filter((item): item is IPendingSignatureItem => item != null);

  const rentProcessIds = new Set(
    rentItems
      .map((item) => item.rent_process_id)
      .filter((id): id is number => id != null)
  );

  const uniqueClickSignItems = clicksignItems.filter(
    (item) => item.rent_process_id == null || !rentProcessIds.has(item.rent_process_id)
  );

  const items = [...rentItems, ...uniqueClickSignItems];
  const rentCounts = countByDocumentStatus(rentItems);
  const clicksignCounts = countByDocumentStatus(uniqueClickSignItems);
  const pagination = normalizePagination(record.pagination);

  // Paginated payloads already include ClickSign in the root totals.
  // Legacy payloads split counts between the root (rent contracts) and clicksign.
  const pendingCount = pagination
    ? asFiniteNumber(record.pending_count) ?? 0
    : (asFiniteNumber(record.pending_count) ?? rentCounts.pending) +
      (asFiniteNumber(clicksign?.pending_count) ?? clicksignCounts.pending);
  const signedCount = pagination
    ? asFiniteNumber(record.signed_count) ?? 0
    : (asFiniteNumber(record.signed_count) ?? rentCounts.signed) +
      (asFiniteNumber(clicksign?.signed_count) ?? clicksignCounts.signed);
  const canceledCount = pagination
    ? asFiniteNumber(record.canceled_count) ?? asFiniteNumber(clicksign?.canceled_count) ?? 0
    : asFiniteNumber(clicksign?.canceled_count) ?? clicksignCounts.canceled;
  const total = pagination
    ? asFiniteNumber(record.total) ?? pendingCount + signedCount
    : asFiniteNumber(record.total) != null || asFiniteNumber(clicksign?.total) != null
      ? (asFiniteNumber(record.total) ?? 0) + (asFiniteNumber(clicksign?.total) ?? 0)
      : items.length;

  return {
    api_version: typeof record.api_version === "string" ? record.api_version : undefined,
    total,
    pending_count: pendingCount,
    signed_count: signedCount,
    canceled_count: canceledCount,
    items,
    pagination,
    signing: normalizeSigning(record.signing),
    clicksign_configured:
      typeof clicksign?.configured === "boolean" ? clicksign.configured : null,
    clicksign_error: asNullableString(clicksign?.error),
  };
}
